// Builds and downloads a CSV of the outline's requirements, client-side, from data already in
// Redux. Kept separate from the component so the (pure) CSV construction is unit-testable without
// touching the DOM.

import { downloadFileFromResponse } from "apis/localhost";

const CSV_HEADERS = [
  "Ref",
  "Requirement",
  "Categories",
  "Verification",
  "Covered By",
  "Source Document",
  "Section Ref",
  "Verbatim Quote",
];

// Excel, Google Sheets and LibreOffice evaluate a cell whose first non-space character is =, +, -, @,
// or a tab/CR/LF. Prefix those with a single quote so the literal text renders (CSV injection);
// signed plain numbers and a bare sign stay numeric.
const FORMULA_TRIGGER = /^ *[=+\-@\t\r\n]/;
const SIGNED_PLAIN_NUMBER = /^ *[+-][\d.,]*%?$/;

const escapeCell = (value) => {
  let text = value == null ? "" : String(value);
  if (FORMULA_TRIGGER.test(text) && !SIGNED_PLAIN_NUMBER.test(text)) {
    text = `'${text}`;
  }
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/**
 * Produces the CSV text for the requirements list. `coverage.coveringByPoint` is keyed by point id,
 * which is the requirement's refLabel, so covering section titles join in directly. Rows are ordered
 * by orderIndex to match the checklist. Categories and covering sections are semicolon-joined so
 * commas inside them never fight the CSV delimiter.
 */
export const buildRequirementsCsv = (
  requirements = [],
  coverage = {},
  documentLabelsById = {}
) => {
  const coveringByPoint = coverage?.coveringByPoint || {};
  const ordered = [...requirements].sort(
    (a, b) => (a?.orderIndex ?? 0) - (b?.orderIndex ?? 0)
  );
  const lines = [CSV_HEADERS.map(escapeCell).join(",")];
  ordered.forEach((requirement) => {
    const covering = coveringByPoint[requirement.refLabel] || [];
    const cells = [
      requirement.refLabel,
      requirement.restatedText,
      (requirement.categories || []).join("; "),
      requirement.verification,
      covering.join("; "),
      documentLabelsById[requirement.sourceDocumentId] || "",
      requirement.sectionRef,
      requirement.verbatimQuote,
    ];
    lines.push(cells.map(escapeCell).join(","));
  });
  // CRLF line endings — the CSV convention Excel expects.
  return lines.join("\r\n");
};

// Slugifies the owner name into a safe filename stem, falling back to a generic name.
// The first replace collapses every run of non-alphanumerics to a single "-", so the trim only ever
// has one leading and one trailing hyphen to remove. Matching a single "-" rather than "-+" keeps
// the pattern free of the backtracking that makes an unanchored "-+$" quadratic (Sonar S5852).
const toFileStem = (ownerName) => {
  const slug = (ownerName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug ? `${slug}-requirements` : "requirements";
};

/**
 * Triggers a browser download of the CSV as `<owner>-requirements.csv`. Reuses the shared
 * blob-download helper for the DOM plumbing; the leading BOM makes Excel decode the UTF-8 content
 * correctly instead of mangling accented characters and smart quotes.
 */
export const downloadRequirementsCsv = (csv, ownerName) => {
  const withBom = `\ufeff${csv}`;
  downloadFileFromResponse(withBom, `${toFileStem(ownerName)}.csv`, {
    type: "text/csv;charset=utf-8;",
  });
};
